import { DestroyRef, Injectable, inject, signal } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

/** Shown in the top header when the browser fires `offline` during an active session. */
export const OFFLINE_HEADER_MESSAGE = "You're offline. Trying to connect…";

/**
 * Tracks `navigator.onLine` and browser `online` / `offline` events.
 * The offline header is hidden on every page load and shown only after an `offline` event.
 */
@Injectable({ providedIn: 'root' })
export class NetworkConnectivityService {
  private readonly destroyRef = inject(DestroyRef);

  readonly isOnline = signal(this.readNavigatorOnline());

  readonly showOfflineHeader = signal(false);

  constructor() {
    const onOnline = () => {
      this.isOnline.set(true);
      this.showOfflineHeader.set(false);
    };
    const onOffline = () => {
      this.isOnline.set(false);
      this.showOfflineHeader.set(true);
    };
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    this.destroyRef.onDestroy(() => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
    });
  }

  /** True when an HTTP call failed because the network is unavailable. */
  isOfflineHttpError(err: unknown): boolean {
    return err instanceof HttpErrorResponse && err.status === 0;
  }

  private readNavigatorOnline(): boolean {
    return typeof navigator === 'undefined' ? true : navigator.onLine !== false;
  }
}
